import { ArrowRight } from "@phosphor-icons/react"
import { AnimatePresence, motion } from "motion/react"
import { Fragment, useEffect, useMemo, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"
import Select, { type ReactSelectOption } from "~/components/reusable/Select"
import { Switch } from "~/components/reusable/Switch"
import { Tooltip } from "~/components/reusable/Tooltip"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { cn } from "~/utils/helpers"

type Props = {
  onDemandMapChange: (demandMap: Record<string, string[]>) => void
  warehouseIds?: string[]
}

export default function AdvancedDemandMapping({ warehouseIds, onDemandMapChange }: Props) {
  const [open, setOpen] = useState(false)
  const warehouses = usePrimaryWarehouses()
  const [demandMap, setDemandMap] = useState<Record<string, string[]>>({})

  const warehousesToMap = useMemo(() => {
    const selectedWarehouses = warehouses
      .filter((wh) => warehouseIds?.includes(wh.id))
      .map((x) => x.id)
    const alreadyMapped = Object.values(demandMap).flat()
    return warehouses.filter(
      (wh) => !selectedWarehouses.includes(wh.id) && !alreadyMapped.includes(wh.id),
    )
  }, [warehouseIds, warehouses, demandMap])

  const setDemandForWarehouse = (warehouseId: string, demandIds: string[]) => {
    const newDemandMap = { ...demandMap, [warehouseId]: demandIds }
    setDemandMap(newDemandMap)
    onDemandMapChange(newDemandMap)
  }

  const options = useMemo(() => {
    return warehousesToMap.map((wh) => ({
      value: wh.id,
      label: wh.name,
    })) as ReactSelectOption<string>[]
  }, [warehousesToMap])

  const handleOpenChange = (open: boolean) => {
    if (open === true && (!warehouseIds?.length || warehouseIds.length === warehouses.length))
      return
    setOpen(open)
    if (!open) {
      setDemandMap({})
      onDemandMapChange({})
    }
  }

  useEffect(() => {
    if (!warehouseIds?.length || warehouseIds.length === warehouses.length) {
      handleOpenChange(false)
    }
    setDemandMap({})
    onDemandMapChange({})
  }, [warehouseIds])

  const tooltipTitle = useMemo(() => {
    if (!warehouseIds?.length) {
      return "No warehouses selected. Please select warehouses to enable advanced demand mapping."
    }
    if (warehouseIds.length === warehouses.length) {
      return "All warehouses are selected. Advanced demand mapping is disabled."
    }
    return undefined
  }, [warehouseIds, warehouses.length])

  if (warehouses.length < 2) return null

  return (
    <Card>
      <Tooltip title={tooltipTitle}>
        <CardHeader
          className={cn("flex flex-row justify-between cursor-not-allowed bg-subtle", {
            "cursor-pointer": warehouseIds?.length && warehouseIds.length != warehouses.length,
          })}
          onClick={() => handleOpenChange(!open)}
        >
          <CardTitle>Advanced Demand Mapping</CardTitle>
          <Switch
            checked={open}
            onCheckedChange={(checked) => handleOpenChange(checked)}
            className="ml-2"
            disabled={!warehouseIds?.length || warehouseIds.length === warehouses.length}
          />
        </CardHeader>
      </Tooltip>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CardContent className="p-4 bg-subtle">
              <div className="grid grid-cols-2 gap-2 gap-x-4 paragraph-md relative">
                <div className="text-fg-subtle label-base">Warehouse</div>
                <div className="text-fg-subtle label-base">
                  <p>Demand</p>
                </div>
                {warehouseIds?.map((wh) => (
                  <Fragment key={wh}>
                    <div className="flex flex-row items-center justify-between gap-2 grow">
                      <div className="text-fg-base paragraph-base">
                        {warehouses.find((x) => x.id == wh)?.name}
                      </div>
                      <ArrowRight className="text-fg-muted" />
                    </div>
                    <div id="select-portal">
                      <Select
                        options={options}
                        placeholder="Select warehouses"
                        className="w-full"
                        onChange={(selected) => {
                          const selectedValues = selected ? selected.map((s) => s.value) : []
                          setDemandForWarehouse(
                            wh,
                            selectedValues.filter((id) => id !== wh),
                          )
                        }}
                        value={[
                          {
                            value: wh,
                            label: warehouses.find((x) => x.id === wh)?.name || wh,
                            isFixed: true,
                          },
                          ...(demandMap[wh]?.map((id) => ({
                            value: id,
                            label: warehouses.find((x) => x.id === id)?.name || id,
                          })) ?? []),
                        ]}
                        isClearable
                        isMulti
                        truncate
                      />
                    </div>
                  </Fragment>
                ))}
              </div>
            </CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  )
}
